import { z } from 'zod';

/**
 * Fleet dashboard DTOs.
 *
 * Mirrors db/schema/302_views_fleet.sql:
 *   v_fleet_totals    → fleetTotalsSchema       (single row)
 *   v_fleet_by_seller → fleetBySellerItemSchema (one row per seller_org)
 *   v_fleet_by_class  → fleetByClassItemSchema  (one row per asset_class)
 *
 * Lifecycle counts mirror the real seeded ids (active, idle, in_transit,
 * decommissioned, disposed, quarantined) — the originally-shipped view
 * referenced incorrect ids and counted 0 across the board; fixed in
 * PR-4.
 */

export const fleetTotalsSchema = z.object({
  // SIL-1648: assetCount is UNITS (sum of asset.quantity); lineCount is the
  // number of inventory lines ("N units in M lines").
  assetCount: z.number().int(),
  lineCount: z.number().int(),
  totalFmvMedUsd: z.coerce.number(),
  totalNetRecoveryUsd: z.coerce.number(),
  avgConfidenceScore: z.coerce.number().nullable(),
  sellerCount: z.number().int(),
  assetClassCount: z.number().int(),
});
export type FleetTotals = z.infer<typeof fleetTotalsSchema>;

export const fleetBySellerItemSchema = z.object({
  sellerOrgId: z.string().uuid(),
  sellerLegalName: z.string(),
  sellerShortName: z.string().nullable(),
  assetCount: z.number().int(),
  lineCount: z.number().int(),
  totalFmvMedUsd: z.coerce.number(),
  totalNetRecoveryUsd: z.coerce.number(),
  activeCount: z.number().int(),
  idleCount: z.number().int(),
  inTransitCount: z.number().int(),
  decommissionedCount: z.number().int(),
  disposedCount: z.number().int(),
  quarantinedCount: z.number().int(),
  condLikeNewCount: z.number().int(),
  condNormalCount: z.number().int(),
  condHighWearCount: z.number().int(),
  condUnknownCount: z.number().int(),
});
export type FleetBySellerItem = z.infer<typeof fleetBySellerItemSchema>;

export const fleetByClassItemSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  assetCount: z.number().int(),
  lineCount: z.number().int(),
  totalFmvMedUsd: z.coerce.number(),
  avgConfidenceScore: z.coerce.number().nullable(),
  activeCount: z.number().int(),
  idleCount: z.number().int(),
  inTransitCount: z.number().int(),
  decommissionedCount: z.number().int(),
  disposedCount: z.number().int(),
  quarantinedCount: z.number().int(),
});
export type FleetByClassItem = z.infer<typeof fleetByClassItemSchema>;

export const fleetSummaryResponseSchema = z.object({
  totals: fleetTotalsSchema,
  bySeller: z.array(fleetBySellerItemSchema),
  byClass: z.array(fleetByClassItemSchema),
});
export type FleetSummaryResponse = z.infer<typeof fleetSummaryResponseSchema>;
